import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { addBothAdvancedCourses, addPythonAdvancedCourse, addDataAnalyticsCourse } from '../../utils/addAdvancedCourses';
import { pythonAdvancedCourseData } from '../../utils/pythonAdvancedCourse';
import { dataAnalyticsCourseData } from '../../utils/dataAnalyticsCourse';
import './AddPythonCourse.css';

const AddAdvancedCourses = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [addingCourse, setAddingCourse] = useState(null); // 'python', 'analytics', or 'both'

  const handleAddCourse = async (courseType) => {
    if (!user || user.type !== 'admin') {
      setMessage('❌ Only admins can add courses.');
      return;
    }

    setLoading(true);
    setAddingCourse(courseType);
    setMessage('');

    try {
      const creatorId = user.id;
      const creatorName = user.name || user.email?.split('@')[0] || 'Admin';
      const creatorEmail = user.email || '';

      if (courseType === 'both') {
        await addBothAdvancedCourses(creatorId, creatorName, creatorEmail);
        setMessage('✅ Both courses created successfully with all interactive components!');
      } else if (courseType === 'python') {
        await addPythonAdvancedCourse(creatorId, creatorName, creatorEmail);
        setMessage('✅ Python Advanced course created successfully with all interactive components!');
      } else if (courseType === 'analytics') {
        await addDataAnalyticsCourse(creatorId, creatorName, creatorEmail);
        setMessage('✅ Data Analytics course created successfully with all interactive components!');
      }
    } catch (error) {
      console.error('Error adding course:', error);
      setMessage(`❌ Error: ${error.message || 'Failed to add course'}`);
    } finally {
      setLoading(false);
      setAddingCourse(null);
    }
  };

  if (!user || user.type !== 'admin') {
    return <div>Only admins can access this page.</div>;
  }

  return (
    <div className="add-python-course">
      <h1>🚀 Add Advanced Courses</h1>
      
      {/* Python Advanced Course */}
      <div className="course-preview" style={{ marginBottom: '30px' }}>
        <h2>{pythonAdvancedCourseData.title}</h2>
        <p className="course-description">{pythonAdvancedCourseData.description}</p>
        <div className="topics-preview">
          <h3>Topics ({pythonAdvancedCourseData.topics.length}):</h3>
          <ul>
            {pythonAdvancedCourseData.topics.map((topic, idx) => (
              <li key={idx}>
                <strong>{topic.name}</strong>
                <span className="hours-badge">{topic.hours}h</span>
                <div className="topic-description">{topic.description}</div>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ marginTop: '15px', padding: '10px', background: '#f0f4ff', borderRadius: '8px' }}>
          <strong>Interactive Components:</strong> Code Editor, Code Challenge, Code Review, API Explorer, 
          Database Designer, Quiz Master, Virtual Lab, Whiteboard, Portfolio Builder, Progress Tracker
        </div>
        <button 
          onClick={() => handleAddCourse('python')} 
          disabled={loading}
          className="add-course-btn"
          style={{ marginTop: '15px' }}
        >
          {loading && addingCourse === 'python' ? '⏳ Adding Course...' : '🐍 Add Python Advanced Course'}
        </button>
      </div>

      {/* Data Analytics Course */}
      <div className="course-preview" style={{ marginBottom: '30px' }}>
        <h2>{dataAnalyticsCourseData.title}</h2>
        <p className="course-description">{dataAnalyticsCourseData.description}</p>
        <div className="topics-preview">
          <h3>Topics ({dataAnalyticsCourseData.topics.length}):</h3>
          <ul>
            {dataAnalyticsCourseData.topics.map((topic, idx) => (
              <li key={idx}>
                <strong>{topic.name}</strong>
                <span className="hours-badge">{topic.hours}h</span>
                <div className="topic-description">{topic.description}</div>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ marginTop: '15px', padding: '10px', background: '#f0f4ff', borderRadius: '8px' }}>
          <strong>Interactive Components:</strong> Database Designer, Data Visualizer, API Explorer, 
          Code Editor, Code Challenge, Quiz Master, Virtual Lab, Whiteboard, 
          Portfolio Builder, Progress Tracker
        </div>
        <button 
          onClick={() => handleAddCourse('analytics')} 
          disabled={loading}
          className="add-course-btn"
          style={{ marginTop: '15px' }}
        >
          {loading && addingCourse === 'analytics' ? '⏳ Adding Course...' : '📊 Add Data Analytics Course'}
        </button>
      </div>

      {/* Add Both Button */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button 
          onClick={() => handleAddCourse('both')} 
          disabled={loading}
          className="add-course-btn"
          style={{ 
            fontSize: '18px', 
            padding: '15px 30px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          {loading && addingCourse === 'both' ? '⏳ Adding Both Courses...' : '✨ Add Both Courses'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`} style={{ marginTop: '20px' }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AddAdvancedCourses;

